import { InspectionTemplateScope, InspectionTemplateUsage } from './inspection-template.model';

/**
 * The only bundle schema this client understands. The server rejects any other version with a 400, so the
 * client refuses it up front to spare the staff admin a round trip with a file that can never import.
 */
export const CONFIG_BUNDLE_SCHEMA_VERSION = 1;

/**
 * Client-side upload cap for a configuration bundle, matching the server's 5 MB request body limit so an
 * oversize file is rejected before it is read into memory rather than after a failed POST.
 */
export const CONFIG_BUNDLE_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Per-template outcome of an import preview. Only `NotFound` blocks the commit; `Skip` and `Conflict` are
 * left out of the commit but let the rest proceed, because an import never overwrites existing templates.
 */
export enum ConfigTemplateResult {
    Create = 'Create',
    Skip = 'Skip',
    Conflict = 'Conflict',
    NotFound = 'NotFound',
}

/** Outcome of setting a job category's default inspection template during an import. */
export enum ConfigJobCategoryDefaultResult {
    Set = 'Set',
    AlreadySet = 'AlreadySet',
    TemplateNotCreated = 'TemplateNotCreated',
    NotFound = 'NotFound',
}

/** The kind of transfer recorded in a tenant's configuration transfer history. */
export enum ConfigTransferAction {
    Export = 'Export',
    Import = 'Import',
}

/** Where and by whom a bundle was produced; carried in the bundle and echoed back by the preview. */
export interface ConfigBundleSource {
    environment: string;
    tenantName: string;
    exportedAt: Date | string;
    exportedBy: string;
}

/**
 * A branch as it existed in the source environment. The bundle never carries database ids, so branches are
 * referenced by an opaque bundle-local key and matched to target branches by the staff admin at import time.
 */
export interface ConfigBundleBranch {
    key: string;
    name: string;
}

/** A job category referenced by the bundle's templates; matched by code in the target tenant, never created. */
export interface ConfigBundleJobCategoryRef {
    key: string;
    code: string;
    name: string;
}

/**
 * An exported inspection template. The client reads only the summary fields it shows and passes everything
 * else (sections, items, note options, behaviour flags) back to the server untouched, so the section tree is
 * typed loosely on purpose: the server owns its shape and validation.
 */
export interface ConfigBundleTemplate {
    key: string;
    name: string;
    scope: InspectionTemplateScope;
    usage: InspectionTemplateUsage;
    branchKey: string;
    sections: unknown[];
    [field: string]: unknown;
}

/** Links a job category to the template it should default to in the target tenant. */
export interface ConfigBundleJobCategoryDefault {
    jobCategoryKey: string;
    templateKey: string;
}

/** A feature flag carried by the bundle; applied at import only when the staff admin opts in. */
export interface ConfigBundleFeatureFlag {
    feature: string;
    enabled: boolean;
}

/**
 * A versioned, id-free tenant configuration bundle (schema version 1: inspection templates). Produced by the
 * export endpoint on one environment and posted verbatim to the preview and commit endpoints on another.
 */
export interface ConfigBundle {
    schemaVersion: number;
    source: ConfigBundleSource;
    branches: ConfigBundleBranch[];
    jobCategoryRefs: ConfigBundleJobCategoryRef[];
    templates: ConfigBundleTemplate[];
    jobCategoryDefaults: ConfigBundleJobCategoryDefault[];
    featureFlags?: ConfigBundleFeatureFlag[] | null;
}

/**
 * Maps one bundle branch onto a branch of the target tenant. A null `targetBranchId` means the branch is
 * skipped, which leaves its templates out of the import.
 */
export interface ConfigBranchMapping {
    bundleBranchKey: string;
    targetBranchId: string | null;
}

/** Body of the stateless, write-free preview call. */
export interface ConfigBundlePreviewRequest {
    bundle: ConfigBundle;
    branchMap: ConfigBranchMapping[];
    applyFeatureFlags: boolean;
}

/**
 * Body of the commit call. `bundleHash` is the hash the preview returned; the server recomputes it from the
 * posted bundle and rejects a mismatch, so the commit can only apply exactly the bundle that was previewed.
 */
export interface ConfigBundleCommitRequest extends ConfigBundlePreviewRequest {
    bundleHash: string;
}

/** One template row of an import preview. */
export interface ConfigTemplatePreview {
    key: string;
    name: string;
    scope: InspectionTemplateScope;
    usage: InspectionTemplateUsage;
    bundleBranchKey: string;
    bundleBranchName: string;
    targetBranchId: string | null;
    targetBranchName: string | null;
    itemCount: number;
    result: ConfigTemplateResult;
    detail: string;
}

/** Whether a job category code referenced by the bundle exists in the target tenant. */
export interface ConfigJobCategoryPreview {
    key: string;
    code: string;
    found: boolean;
}

/** Planned outcome for one job category default-template link. */
export interface ConfigJobCategoryDefaultPreview {
    jobCategoryCode: string;
    templateName: string;
    result: ConfigJobCategoryDefaultResult;
}

/** Per-result template counts shown as the preview's stat tiles. */
export interface ConfigPreviewCounts {
    create: number;
    skip: number;
    conflict: number;
    notFound: number;
}

/** What an import would do, computed without writing anything. */
export interface ConfigBundlePreviewResult {
    bundleHash: string;
    schemaVersion: number;
    source: ConfigBundleSource;
    templates: ConfigTemplatePreview[];
    jobCategories: ConfigJobCategoryPreview[];
    jobCategoryDefaults: ConfigJobCategoryDefaultPreview[];
    featureFlags: ConfigBundleFeatureFlag[];
    counts: ConfigPreviewCounts;
    canCommit: boolean;
    blockingReasons: string[];
}

/** A template the commit created. */
export interface ConfigCreatedTemplate {
    name: string;
    scope: InspectionTemplateScope;
    targetBranchName: string | null;
    itemCount: number;
}

/** Totals of a committed import. */
export interface ConfigCommitCounts {
    created: number;
    skipped: number;
    conflict: number;
    jobCategoryDefaultsSet: number;
    featureFlagsApplied: number;
}

/** Outcome of a committed import; the whole commit ran in one transaction. */
export interface ConfigBundleCommitResult {
    bundleHash: string;
    committedAt: Date | string;
    committedBy: string;
    created: ConfigCreatedTemplate[];
    counts: ConfigCommitCounts;
}

/**
 * One row of a tenant's configuration transfer history. Each export and each committed import writes one;
 * it doubles as the audit record of who moved which bundle where.
 */
export interface ConfigTransferRecord {
    id: string;
    action: ConfigTransferAction;
    performedAt: Date | string;
    performedBy: string;
    sourceTenantName: string;
    sourceEnvironment: string;
    bundleHash: string;
    templateCount: number;
    jobCategoryRefCount: number;
    branchCount: number;
    createdCount: number;
}

/** Template, job category reference and branch totals of an exported bundle, shown on the Export card. */
export interface ConfigExportCounts {
    templates: number;
    jobCategoryRefs: number;
    branches: number;
}
